import { SegmentedControl } from '@hue/design-system';
import { Button, Input, Select } from '@hue/design-system';
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  BookOpen,
  ChevronDown,
  ChevronUp,
  Search,
} from 'lucide-react';
import { Fragment, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { DocumentationFlowDiagram } from './system-documentation-flow-diagram.js';
import {
  documentationAnchorFor,
  documentationFlows,
  documentationPageHelp,
  flowStepTexts,
  type DocumentationPageKey,
} from './system-documentation-flows.js';
import {
  documentationModules,
  type DocumentationModule,
  documentationPrinciples,
  technicalDocumentationPrinciples,
} from './system-documentation-content.js';
import { rangesOf, type DocumentationSearchMode } from './system-documentation-search.js';

/// Altura em que o título de uma área, saltado pelo menu, repousa abaixo da busca fixa (`scroll-mt-44`,
/// 176 px) com folga de arredondamento. A área cujo topo já passou dessa linha é a que está sendo lida.
const READING_LINE = 180;

/// O que a Ajuda percorre na ordem da página: primeiro os fluxos, que atravessam áreas, depois as
/// áreas. A chave vira a âncora `documentation-<chave>`.
const documentationSections = [
  ...documentationFlows.map((flow) => ({ key: `flow-${flow.key}`, title: flow.title })),
  ...documentationModules.map((module) => ({ key: module.key, title: module.title })),
];

function principlesFor(audience: 'user' | 'technical') {
  return audience === 'technical' ? technicalDocumentationPrinciples : documentationPrinciples;
}

/// A Ajuda descreve tarefas; a lista de dados sob responsabilidade é vocabulário de arquitetura e
/// fica somente na referência técnica (ADR 0047).
function featuresFor(module: DocumentationModule, audience: 'user' | 'technical') {
  return audience === 'technical' ? module.owns : module.canDo;
}

/// Os subtítulos fixos de cada área. Entram na busca como qualquer outro texto: quem procura
/// "outras áreas" ou "regras" quer cair no trecho que tem esse nome.
function headingsFor(audience: 'user' | 'technical') {
  const technical = audience === 'technical';
  return {
    principles: technical ? 'Princípios de arquitetura' : 'Antes de começar',
    owns: technical ? 'Dados sob sua responsabilidade' : 'O que você pode fazer',
    rules: 'Regras importantes',
    pages: technical ? 'Telas, endereços e uso' : 'Como usar',
    integrations: technical ? 'Integrações e limites' : 'O que acontece em outras áreas',
  };
}

const FLOW_EYEBROW = 'Fluxo do dia a dia';

function allDocumentationText(audience: 'user' | 'technical') {
  const headings = headingsFor(audience);
  return [
    ['principles-title', headings.principles] as const,
    ...principlesFor(audience).map((text, index) => [`principle-${index}`, text] as const),
    ...documentationFlows.flatMap((flow) => [
      [`flow-${flow.key}-eyebrow`, FLOW_EYEBROW] as const,
      [`flow-${flow.key}-title`, flow.title] as const,
      [`flow-${flow.key}-summary`, flow.summary] as const,
      ...flow.stages.map((text, index) => [`flow-${flow.key}-stage-${index}`, text] as const),
      ...flow.steps.flatMap((step) => flowStepTexts(flow.key, step)),
    ]),
    ...documentationModules.flatMap((module) => [
      [
        `${module.key}-title`,
        audience === 'technical' ? `${module.title} ${module.technicalName}` : module.title,
      ] as const,
      [`${module.key}-summary`, module.summary] as const,
      [`${module.key}-owns-title`, headings.owns] as const,
      ...featuresFor(module, audience).map(
        (text, index) => [`${module.key}-owns-${index}`, text] as const,
      ),
      ...(module.rules?.length
        ? [
            [`${module.key}-rules-title`, headings.rules] as const,
            ...module.rules.map((text, index) => [`${module.key}-rules-${index}`, text] as const),
          ]
        : []),
      [`${module.key}-pages-title`, headings.pages] as const,
      ...module.pages.flatMap((page, pageIndex) => [
        [
          `${module.key}-page-${pageIndex}`,
          audience === 'technical' ? `${page.name} ${page.path}` : page.name,
        ] as const,
        ...page.usage.map(
          (text, usageIndex) => [`${module.key}-page-${pageIndex}-${usageIndex}`, text] as const,
        ),
      ]),
      [`${module.key}-integration-title`, headings.integrations] as const,
      ...module.integrations.map(
        (text, index) => [`${module.key}-integration-${index}`, text] as const,
      ),
    ]),
  ];
}

export function SystemDocumentation({
  audience = 'user',
  from,
  onBack,
  renderOpen,
}: {
  audience?: 'user' | 'technical';
  /// A página de onde a pessoa veio pelo ícone de ajuda.
  from?: DocumentationPageKey | undefined;
  onBack?: () => void;
  /// O link que leva à tela citada num passo; sem ele, o painel não oferece abrir a tela.
  renderOpen?: (page: DocumentationPageKey, label: string) => ReactNode;
}) {
  const [draft, setDraft] = useState('');
  const [query, setQuery] = useState('');
  const [mode, setMode] = useState<DocumentationSearchMode>('words');
  const [active, setActive] = useState(0);
  const [section, setSection] = useState('');
  useReadingSection(setSection);
  const headings = headingsFor(audience);
  const plan = useMemo(() => {
    let cursor = 0;
    const entries = new Map<string, { ranges: ReturnType<typeof rangesOf>; start: number }>();
    for (const [key, text] of allDocumentationText(audience)) {
      const ranges = query ? rangesOf(text, query, mode) : [];
      entries.set(key, { ranges, start: cursor });
      cursor += ranges.length;
    }
    return { entries, total: cursor };
  }, [audience, mode, query]);

  const scrollToMatch = useCallback((index: number) => {
    requestAnimationFrame(() => {
      document.querySelector<HTMLElement>(`[data-documentation-match="${index}"]`)?.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'center',
      });
    });
  }, []);

  const goTo = useCallback(
    (index: number) => {
      const next = (index + plan.total) % plan.total;
      setActive(next);
      scrollToMatch(next);
    },
    [plan.total, scrollToMatch],
  );

  function search() {
    setQuery(draft.trim());
    setActive(0);
  }

  useEffect(() => {
    if (!from) return;
    requestAnimationFrame(() =>
      document.getElementById(documentationAnchorFor(from))?.scrollIntoView({ block: 'start' }),
    );
  }, [from]);

  useEffect(() => {
    if (query && plan.total) scrollToMatch(0);
  }, [mode, plan.total, query, scrollToMatch]);

  const highlighted = (key: string, text: string): ReactNode => {
    const entry = plan.entries.get(key)!;
    if (!entry.ranges.length) return text;
    let cursor = 0;
    return entry.ranges.map((range, index) => {
      const matchIndex = entry.start + index;
      const before = text.slice(cursor, range.start);
      const found = text.slice(range.start, range.end);
      cursor = range.end;
      return (
        <Fragment key={`${range.start}-${range.end}`}>
          {before}
          <mark
            data-documentation-match={matchIndex}
            className={`scroll-mt-44 rounded px-0.5 text-inherit ${
              active === matchIndex ? 'bg-brand-500 text-on-brand' : 'bg-brand-500/25'
            }`}
          >
            {found}
          </mark>
          {index === entry.ranges.length - 1 ? text.slice(cursor) : null}
        </Fragment>
      );
    });
  };

  return (
    <section aria-labelledby="documentation-title" className="min-w-0">
      <header className="border-b border-surface-border pb-8">
        <div className="flex items-start gap-4">
          <span className="grid size-12 shrink-0 place-items-center rounded-2xl bg-brand-500/15 text-brand-400">
            <BookOpen className="size-6" aria-hidden="true" />
          </span>
          <div>
            <h1
              id="documentation-title"
              className="text-3xl font-bold tracking-[-.04em] sm:text-4xl"
            >
              {audience === 'technical' ? 'Documentação técnica' : 'Ajuda do sistema'}
            </h1>
            <p className="mt-3 max-w-3xl text-sm leading-7 text-content-muted sm:text-base">
              {audience === 'technical'
                ? 'Referência de módulos, identificadores, endereços internos e integrações do HUE•System.'
                : 'Aprenda o que cada área faz, quando usá-la e quais regras são importantes no dia a dia. Não é preciso conhecer termos técnicos.'}{' '}
              Use a busca para localizar uma palavra ou uma expressão exata.
            </p>
          </div>
        </div>
      </header>

      {from ? (
        <div className="mt-6 flex flex-wrap items-center gap-3 rounded-2xl border border-brand-500/35 bg-brand-500/10 px-4 py-3">
          <p className="min-w-0 flex-1 text-sm leading-6 text-content-secondary">
            Você veio de{' '}
            <strong className="text-content">{documentationPageHelp[from].name}</strong>.{' '}
            {!documentationPageHelp[from].flow
              ? 'Abaixo está a área dessa página e como usá-la.'
              : documentationFlows.some((flow) =>
                    flow.steps.some((step) => step.pages.includes(from)),
                  )
                ? 'Os passos feitos nessa página estão destacados no fluxo abaixo.'
                : 'Abaixo está o fluxo que começa por ela.'}
          </p>
          {onBack ? (
            <Button variant="outline" size="sm" onClick={onBack}>
              <ArrowLeft className="size-4" aria-hidden="true" />
              Voltar para {documentationPageHelp[from].name}
            </Button>
          ) : null}
        </div>
      ) : null}

      <div className="sticky top-20 z-20 -mx-2 mt-6 rounded-2xl border border-brand-500/25 bg-surface-card/95 p-3 shadow-card backdrop-blur lg:top-4">
        <form
          className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto_auto] lg:items-end"
          onSubmit={(event) => {
            event.preventDefault();
            search();
          }}
        >
          <Input
            label="Buscar na documentação"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={
              mode === 'exact' ? 'Digite a expressão exata' : 'Digite uma ou mais palavras'
            }
            leftIcon={<Search className="size-4" />}
          />
          <SegmentedControl
            label="Modo da busca"
            compact
            value={mode}
            onChange={setMode}
            options={[
              { value: 'words', label: 'Palavras' },
              { value: 'exact', label: 'Texto exato' },
            ]}
          />
          <Button type="submit">Buscar</Button>
        </form>
        <div className="mt-3 flex min-h-10 items-center justify-between gap-3 border-t border-surface-border pt-3">
          <p aria-live="polite" className="text-xs text-content-muted">
            {!query
              ? 'Digite um termo para pesquisar.'
              : plan.total
                ? `${active + 1} de ${plan.total} resultados`
                : 'Nenhum resultado encontrado.'}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={!plan.total}
              onClick={() => goTo(active - 1)}
              aria-label="Resultado anterior"
              className="grid size-10 place-items-center rounded-xl border border-surface-border text-content-secondary hover:border-brand-500/50 hover:text-brand-400 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ChevronUp className="size-4" aria-hidden="true" />
            </button>
            <button
              type="button"
              disabled={!plan.total}
              onClick={() => goTo(active + 1)}
              aria-label="Próximo resultado"
              className="grid size-10 place-items-center rounded-xl border border-surface-border text-content-secondary hover:border-brand-500/50 hover:text-brand-400 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ChevronDown className="size-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      <div className="mt-10 grid gap-10 lg:grid-cols-[220px_minmax(0,1fr)]">
        <aside className="self-start lg:sticky lg:top-52">
          <div className="lg:hidden">
            <Select
              label="Ir para um fluxo ou uma área"
              value={section}
              options={documentationSections.map((item) => ({
                value: item.key,
                label: item.title,
              }))}
              onValueChange={(key) => {
                setSection(key);
                document.getElementById(`documentation-${key}`)!.scrollIntoView({
                  behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
                    ? 'auto'
                    : 'smooth',
                });
              }}
            />
          </div>
          <nav
            aria-label="Áreas da documentação"
            className="hidden border-l border-surface-border lg:block"
          >
            {(
              [
                ['Fluxos do dia a dia', documentationSections.slice(0, documentationFlows.length)],
                ['Áreas', documentationSections.slice(documentationFlows.length)],
              ] as const
            ).map(([group, items]) => (
              <Fragment key={group}>
                <p className="px-4 pb-1 pt-4 text-[11px] font-bold uppercase tracking-[.06em] text-brand-400 first:pt-0">
                  {group}
                </p>
                {items.map((item) => (
                  <a
                    key={item.key}
                    href={`#documentation-${item.key}`}
                    aria-current={section === item.key ? 'location' : undefined}
                    className={`block border-l-2 px-4 py-2 text-sm transition-colors hover:border-brand-500 hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 ${
                      section === item.key
                        ? 'border-brand-500 font-medium text-content'
                        : 'border-transparent text-content-muted'
                    }`}
                  >
                    {item.title}
                  </a>
                ))}
              </Fragment>
            ))}
          </nav>
        </aside>

        <div className="min-w-0 space-y-14">
          <section aria-labelledby="documentation-principles" className="scroll-mt-44">
            <h2 id="documentation-principles" className="text-2xl font-bold tracking-[-.03em]">
              {highlighted('principles-title', headings.principles)}
            </h2>
            <ul className="mt-5 divide-y divide-surface-border border-y border-surface-border">
              {principlesFor(audience).map((text, index) => (
                <li key={text} className="flex gap-3 py-4 text-sm leading-6 text-content-secondary">
                  <span className="mt-2 size-1.5 shrink-0 rounded-full bg-brand-500" />
                  <span>{highlighted(`principle-${index}`, text)}</span>
                </li>
              ))}
            </ul>
          </section>

          {documentationFlows.map((flow) => (
            <article
              key={flow.key}
              id={`documentation-flow-${flow.key}`}
              className="scroll-mt-96 border-t border-surface-border pt-8 lg:scroll-mt-44"
            >
              <p className="text-[11px] font-bold uppercase tracking-[.08em] text-brand-400">
                {highlighted(`flow-${flow.key}-eyebrow`, FLOW_EYEBROW)}
              </p>
              <h2 className="mt-1 text-2xl font-bold tracking-[-.03em]">
                {highlighted(`flow-${flow.key}-title`, flow.title)}
              </h2>
              <p className="mt-4 max-w-3xl text-sm leading-7 text-content-muted">
                {highlighted(`flow-${flow.key}-summary`, flow.summary)}
              </p>
              <DocumentationFlowDiagram
                flow={flow}
                from={from}
                highlighted={highlighted}
                renderOpen={renderOpen}
              />
            </article>
          ))}

          {documentationModules.map((module) => (
            <article
              key={module.key}
              id={`documentation-${module.key}`}
              className="scroll-mt-96 border-t border-surface-border pt-8 lg:scroll-mt-44"
            >
              <h2 className="text-2xl font-bold tracking-[-.03em]">
                {highlighted(
                  `${module.key}-title`,
                  audience === 'technical'
                    ? `${module.title} (${module.technicalName})`
                    : module.title,
                )}
              </h2>
              <p className="mt-4 max-w-3xl text-sm leading-7 text-content-muted">
                {highlighted(`${module.key}-summary`, module.summary)}
              </p>
              <DocumentationList
                title={headings.owns}
                items={featuresFor(module, audience)}
                keyPrefix={`${module.key}-owns`}
                highlighted={highlighted}
              />
              {module.rules?.length ? (
                <DocumentationList
                  title={headings.rules}
                  items={module.rules}
                  keyPrefix={`${module.key}-rules`}
                  highlighted={highlighted}
                />
              ) : null}
              <h3 className="mt-8 text-lg font-bold">
                {highlighted(`${module.key}-pages-title`, headings.pages)}
              </h3>
              <div className="mt-4 space-y-5">
                {module.pages.map((page, pageIndex) => (
                  <section key={page.name} className="border-l-2 border-brand-500/35 pl-5">
                    <h4 className="font-semibold">
                      {highlighted(
                        `${module.key}-page-${pageIndex}`,
                        audience === 'technical' ? `${page.name} — ${page.path}` : page.name,
                      )}
                    </h4>
                    <ul className="mt-2 space-y-2 text-sm leading-6 text-content-secondary">
                      {page.usage.map((text, usageIndex) => (
                        <li key={text} className="flex gap-3">
                          <ArrowDown
                            className="mt-1.5 size-3.5 shrink-0 text-brand-400"
                            aria-hidden="true"
                          />
                          <span>
                            {highlighted(`${module.key}-page-${pageIndex}-${usageIndex}`, text)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
              </div>
              <DocumentationList
                title={headings.integrations}
                items={module.integrations}
                keyPrefix={`${module.key}-integration`}
                highlighted={highlighted}
                icon
              />
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/// Acompanha a leitura: a área marcada no menu lateral (e escolhida na lista do celular) é a última
/// cujo título já subiu até a linha de leitura. No fim da página, as áreas curtas do final nunca
/// alcançam essa linha, então a última área vale enquanto não houver mais para onde rolar.
function useReadingSection(setSection: (key: string) => void) {
  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const page = document.documentElement;
      const atEnd =
        window.scrollY > 0 && window.innerHeight + window.scrollY >= page.scrollHeight - 2;
      let reading = '';
      for (const item of documentationSections) {
        const article = document.getElementById(`documentation-${item.key}`)!;
        if (article.getBoundingClientRect().top <= READING_LINE) reading = item.key;
      }
      setSection(atEnd ? documentationSections.at(-1)!.key : reading);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, [setSection]);
}

function DocumentationList({
  title,
  items,
  keyPrefix,
  highlighted,
  icon = false,
}: {
  title: string;
  items: readonly string[];
  keyPrefix: string;
  highlighted: (key: string, text: string) => ReactNode;
  icon?: boolean;
}) {
  return (
    <section className="mt-8">
      <h3 className="text-lg font-bold">{highlighted(`${keyPrefix}-title`, title)}</h3>
      <ul className="mt-3 space-y-2 text-sm leading-6 text-content-secondary">
        {items.map((text, index) => (
          <li key={text} className="flex gap-3">
            {icon ? (
              <ArrowUp className="mt-1.5 size-3.5 shrink-0 text-brand-400" aria-hidden="true" />
            ) : (
              <span className="mt-2 size-1.5 shrink-0 rounded-full bg-brand-500" />
            )}
            <span>{highlighted(`${keyPrefix}-${index}`, text)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
